import { Loader } from '@progress/kendo-react-indicators';
import { ExpansionPanel, ExpansionPanelContent } from 'shared/layout/ExpansionPanel';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import BaseGrid from '../../../shared/components/grids/BaseGrid/BaseGrid';
import { BaseCell } from '../../../shared/components/grids/Cells';
import BaseIcon from '../../../shared/components/icons/BaseIcon';
import { ColumnMenu } from './ColumnMenu';
import { showModal, hideModal } from 'src/slices/modal';

const SpreadsheetPreviewer = ({
  previewingSheet,
  data,
  isExpanded = true,
  enableMapping = false,
  mappings,
  mappingsSet = [],
  setMappingsSet,
  isLoading,
  presetValidation,
  isValidating,
}) => {
  const [columns, setColumns] = useState([]);
  const [expanded, setExpanded] = useState(isExpanded);
  const dispatch = useDispatch();

  useEffect(() => {
    setColumns(
      previewingSheet?.headers?.map((h) => {
        const mapping = mappingsSet.find((m) => m.column === h || m.displayName === h);
        const title = `${h} ${
          mapping && mapping.displayName === h ? `(${mapping.column})` : mapping ? `(${mapping.displayName})` : ''
        }`;
        return {
          title,
          field: h,
          cell: ({ dataItem }) => (
            <BaseCell
              style={{
                whiteSpace: 'nowrap',
                backgroundColor: h === mapping?.column || h === mapping?.displayName ? '#31436e' : '',
                color: h === mapping?.column || h === mapping?.displayName ? 'white' : '',
              }}
            >
              {dataItem[h]}
            </BaseCell>
          ),
        };
      }),
    );
  }, [mappingsSet]);

  const handleMappingSet = (mapping) => {
    setMappingsSet([...mappingsSet.filter((m) => m.column !== mapping.column), mapping]);
  };

  const severeErrors = presetValidation?.exceptions?.filter((p) => p.issue === 1) || [];
  const isValid = severeErrors.length === 0;

  const showValidationModal = () => {
    dispatch(
      showModal({
        type: 'PRESET_VALIDATION_MODAL',
        props: {
          data: severeErrors,
          onClose: () => {
            dispatch(hideModal());
          },
        },
      }),
    );
  };

  const title = () => {
    const text = previewingSheet?.name
      ? `Preview for ${data?.fileName} (${previewingSheet?.name}) - ${previewingSheet?.totalRows} records`
      : `Preview for ${data?.fileName} - ${previewingSheet?.totalRows} records`;

    const Icon = () =>
      presetValidation ? (
        <div onClick={() => (!isValid ? showValidationModal() : null)}>
          <BaseIcon
            name={isValid ? 'check' : 'warning'}
            style={{ fontSize: '24px', marginLeft: '1rem', color: isValid ? 'green' : 'red' }}
          />
          {isValid && <span style={{ color: 'green', marginLeft: '0.5rem' }}>Selected preset is valid.</span>}
          {!isValid && (
            <span style={{ color: 'red', marginLeft: '0.5rem' }}>Selected preset has validation issues.</span>
          )}
        </div>
      ) : (
        <></>
      );

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        {text}
        <Icon />
      </div>
    );
  };

  if (isLoading || isValidating) {
    return <Loader />;
  }

  return (
    <ExpansionPanel
      title={title()}
      subtitle={data?.isPdf ? 'Showing Mapped PDF Values' : 'Showing the first 15 rows'}
      expanded={expanded}
      tabIndex={0}
      className='order-preview'
      key='preview-grid'
      onAction={({ expanded }) => setExpanded(!expanded)}
    >
      {expanded && (
        <ExpansionPanelContent style={{ overflow: 'auto' }}>
          <BaseGrid
            data={previewingSheet && previewingSheet.rows ? previewingSheet.rows : []}
            columns={columns || []}
            hideActionsCell
            pageable={false}
            scrollable='none'
            resizable
            columnMenu={
              enableMapping
                ? (props) => (
                    <ColumnMenu
                      mappings={mappings}
                      mappingsSet={mappingsSet}
                      setMapping={handleMappingSet}
                      {...props}
                    />
                  )
                : null
            }
          />
        </ExpansionPanelContent>
      )}
    </ExpansionPanel>
  );
};

export default SpreadsheetPreviewer;
